/**
 * Event Utilities
 * 
 * Shared utility functions for event data processing and manipulation.
 * Eliminates repeated logic across multiple page components.
 */

import { PAGE_EVENTS } from '../data/eventsData';

/**
 * Groups events by their date
 * @param {Array} events - Array of event objects
 * @returns {Object} Events grouped by date
 */
export const groupEventsByDate = (events) => {
  return events.reduce((acc, event) => {
    const date = event.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});
};

/**
 * Sorts events within each date group by time
 * @param {Object} groupedEvents - Events grouped by date
 * @returns {Object} Events with sorted times within each date
 */
export const sortEventsByTime = (groupedEvents) => {
  Object.keys(groupedEvents).forEach(date => {
    groupedEvents[date].sort((a, b) => {
      const timeA = new Date(`1970-01-01 ${a.time.split(' – ')[0]}`);
      const timeB = new Date(`1970-01-01 ${b.time.split(' – ')[0]}`);
      return timeA - timeB;
    });
  });
  return groupedEvents;
};

/**
 * Gets all events and processes them for display
 * @returns {Array} Processed and sorted events
 */
export const getProcessedEvents = () => {
  const allEvents = PAGE_EVENTS.home();
  
  // Group events by date and sort by time
  const groupedEvents = groupEventsByDate(allEvents);
  const sortedGroupedEvents = sortEventsByTime(groupedEvents);
  
  // Convert back to flat array maintaining date order
  const sortedEvents = [];
  Object.keys(sortedGroupedEvents)
    .sort((a, b) => new Date(a) - new Date(b))
    .forEach(date => {
      sortedEvents.push(...sortedGroupedEvents[date]);
    });

  return sortedEvents;
};

/**
 * Gets date header information for FullAgenda display
 * @param {string} date - Date string
 * @returns {Object} Title and ornament for the date
 */
export const getDateHeaderInfo = (date) => {
  if (date === 'Saturday, 22nd November 2025') {
    return { title: 'Pre-Wedding Ceremonies', ornament: '🌷' };
  } else if (date === 'Monday, 24th November 2025') {
    return { title: 'Pre-Wedding Celebrations', ornament: '✨' };
  } else if (date === 'Tuesday, 25th November 2025') {
    return { title: 'Wedding Day & Reception', ornament: '💕' };
  } else {
    return { title: 'Wedding Events', ornament: '🌸' };
  }
};